import {
  DocumentTypeElement,
  IirdsMetadataRdf,
  InformationUnitElement,
  RenditionElement
} from '@enduradocs/iirds-parser';
import {
  Constructor,
} from '@rxap/utilities';
import { VirtualFileLike } from '@rxap/fs';
import FlexSearch, { StoreOption } from 'flexsearch';
import { SearchResultDocument } from './search-types';

/**
 * Structure of the document stored in the FlexSearch index.
 * Aligned with SearchResultDocument for interchangeable use.
 */
export type IirdsFlexsearchDocument = SearchResultDocument;

/**
 * Options for creating the iiRDS FlexSearch index.
 */
export interface IirdsIndexOptions<Store extends StoreOption = false> {
  /**
   * Filter InformationUnits by their IRI (about).
   * If provided, only information units that match the filter will be indexed.
   */
  filter?: (iu: InformationUnitElement) => boolean;
  /**
   * A list of IRIs to include. This is a shortcut for a simple filter.
   */
  includeIris?: string[];
  /**
   * Select which rendition to use for content extraction.
   * If not provided, it will prefer 'application/xhtml+xml' or the first available rendition.
   */
  selectRendition?: (renditions: RenditionElement[]) => RenditionElement | null;
  progressCallback?: (counter: number, total: number) => void;
  /**
   * Called when a unit's content fails to load (e.g. a transient IPFS/CID
   * fetch error). The unit is skipped rather than aborting the whole batch —
   * see the try/catch around the content fetch below.
   */
  onUnitError?: (unit: InformationUnitElement, error: unknown) => void;
}

/**
 * Creates an empty FlexSearch Document index configured for iiRDS documents.
 *
 * This is the single source of truth for the document configuration (id, indexed
 * fields and `store: true`). It is used both when building an index from scratch
 * and when constructing the target index into which a persisted index is imported
 * — the import routing (`field.key` → `index[field]`) requires the restore target
 * to share the exact same field configuration, so both paths must use this factory.
 *
 * Called from the FlexSearch Web Worker (`flexsearch-worker-host.ts`) — this
 * function is pure (no Angular/DOM dependency) and safe to run there.
 *
 * @param flexsearchOptions Additional FlexSearch options merged on top of the defaults
 * @param documentType The FlexSearch Document type to use (default: FlexSearch.Document)
 */
export function CreateEmptyIirdsFlexsearchIndex<
  Doc extends IirdsFlexsearchDocument = IirdsFlexsearchDocument,
  Store extends StoreOption = false,
>(
  flexsearchOptions?: FlexSearch.IndexOptions<Doc, Store>,
  documentType: Constructor<FlexSearch.Document<Doc, Store>> = FlexSearch.Document,
): FlexSearch.Document<Doc, Store> {
  return new documentType({
    document: {
      id: 'id',
      index: [
        'title',
        'content',
      ],
      store: true,
    },
    ...flexsearchOptions,
  });
}

/**
 * Default logic for selecting a rendition for text extraction.
 * Prefers HTML or XML formats if present.
 */
export function defaultSelectRendition(
  renditions: RenditionElement[],
): RenditionElement | null {
  if (renditions.length === 0) {
    return null;
  }
  return (
    renditions.find((r) => r.format.toLowerCase().includes('markdown')) ??
    renditions.find((r) => r.format.toLowerCase().includes('text')) ??
    renditions.find(
      (r) =>
        r.format.toLowerCase().includes('html') ||
        r.format.toLowerCase().includes('xml'),
    ) ??
    renditions.find((r) => r.format.toLowerCase().includes('pdf')) ??
    null
  );
}

/** Which extraction path a {@link BatchUnit}'s payload requires. */
export type BatchUnitContentType = 'pdf' | 'xhtml' | 'text';

/**
 * Determines the extraction path for a rendition's format, mirroring the
 * previous `defaultExtractor`'s format checks. Returns `null` for formats with
 * no known extraction path (the unit/rendition is skipped, as `defaultExtractor`
 * used to do by returning `null`).
 */
export function resolveContentType(format: string): BatchUnitContentType | null {
  const value = format.toLowerCase();
  if (value === 'application/pdf') {
    return 'pdf';
  }
  if (value === 'application/xhtml+xml') {
    return 'xhtml';
  }
  if (['text', 'markdown', 'xhtml', 'html', 'xml'].some((item) => value.includes(item))) {
    return 'text';
  }
  return null;
}

/** Plain per-unit fields carried alongside the extracted content into a FlexSearch doc. */
export interface BatchUnitMetadata {
  iri: string;
  title: string;
  /** Every language this unit should be indexed under (content is shared across all of them). */
  languages: string[];
  format: string;
  source: string;
  informationSubjects: string[];
  informationTypes: string[];
  documentTypes: string[];
  productMetadata: string[];
  components: string[];
  informationTypes_labels: string[];
  productMetadata_labels: string[];
  renditions: Array<{ format: string; source: string }>;
  identities: Array<{ domain: string; identifier: string }>;
}

/**
 * One information unit's already-gathered rendition payload, ready to cross
 * the worker boundary: plain data plus either raw bytes (PDF) or text
 * (XHTML/plain text) — never the live `RenditionElement`/`InformationUnitElement`
 * class instances themselves.
 */
export interface BatchUnit {
  contentType: BatchUnitContentType;
  /** Raw bytes for PDF (transferable), or the already-loaded rendition text otherwise. */
  payload: ArrayBuffer | string;
  metadata: BatchUnitMetadata;
}

function isDefined<T>(value: T | undefined | null): value is T {
  return value !== undefined && value !== null;
}

async function virtualFileToText(file: VirtualFileLike): Promise<string> {
  if (file.getText) {
    return file.getText();
  }
  const content = await file.getContent();
  if (typeof content === 'string') {
    return content;
  }
  return new TextDecoder().decode(await content.arrayBuffer());
}

/**
 * Walks an iiRDS metamodel and gathers a plain, worker-safe batch of
 * information-unit content ready to be indexed. This is the main-thread half
 * of what `CreateIirdsFlexsearchIndex` used to do in one pass — the other half
 * (extraction + `index.add()`) now runs in the FlexSearch Web Worker
 * (`flexsearch-worker-host.ts`), since it needs no live class-instance access
 * and is the expensive part.
 *
 * Rendition bytes/text are already fully in memory post-unzip (`getFile().data`
 * / `.getText()` are cheap property reads, not I/O), so gathering them here
 * costs nothing; only the extraction + tokenization that follows is expensive.
 *
 * @param rdf The parsed iiRDS metamodel (RDF)
 * @param options Batch-building options (filter, rendition selection, progress)
 */
export async function BuildFlexsearchBatch(
  rdf: IirdsMetadataRdf,
  options: IirdsIndexOptions = {},
): Promise<BatchUnit[]> {
  const units = rdf.informationUnitList ?? [];
  const includeIris = options.includeIris ? new Set(options.includeIris) : null;
  const batch: BatchUnit[] = [];
  let counter = 0;

  for (const unit of units) {
    options.progressCallback?.(counter++, units.length);
    if (includeIris && !includeIris.has(unit.about)) {
      continue;
    }
    if (options.filter && !options.filter(unit)) {
      continue;
    }

    const rendition = (options.selectRendition ?? defaultSelectRendition)(
      unit.renditionList ?? [],
    );
    if (!rendition) {
      continue;
    }

    const contentType = resolveContentType(rendition.format);
    if (!contentType) {
      continue;
    }

    let payload: ArrayBuffer | string;
    try {
      const file = rendition.getFile();
      payload =
        contentType === 'pdf' ? await file.data : await virtualFileToText(file);
    } catch (error) {
      // A single file failing to load (e.g. an IPFS/CID fetch error) must not
      // abort the whole batch — skip just this unit instead.
      options.onUnitError?.(unit, error);
      continue;
    }

    const languages = unit.languageList?.length
      ? unit.languageList
      : rdf.package.languageList ?? [];

    batch.push({
      contentType,
      payload,
      metadata: {
        iri: unit.about,
        title: unit.title ?? '',
        languages,
        format: rendition.format,
        source: rendition.source,
        informationSubjects:
          unit.subjectList?.map((s) => s.about)?.filter(isDefined) || [],
        informationTypes:
          unit.informationTypeList?.map((i) => i.about)?.filter(isDefined) || [],
        documentTypes:
          unit.informationTypeList
            ?.filter((it) => it instanceof DocumentTypeElement)
            ?.map((d) => d.about)
            ?.filter(isDefined) || [],
        productMetadata:
          unit.productMetadataList?.map((p) => p.about)?.filter(isDefined) || [],
        components:
          unit.componentList?.map((c) => c.about)?.filter(isDefined) || [],
        informationTypes_labels:
          unit.informationTypeList?.map((i) => i.getLabel().value) || [],
        productMetadata_labels:
          unit.productMetadataList?.map((p) => p.getLabel().value) || [],
        renditions:
          unit.renditionList?.map((r) => ({
            format: r.format,
            source: r.source,
          })) || [],
        identities:
          (unit as any).identityList?.map((i: any) => ({
            domain: i.domain,
            identifier: i.identifier,
          })) || [],
      },
    });
  }

  return batch;
}

/**
 * Creates a basic FlexSearch Index for simple string searching.
 */
export function CreateFlexsearchIndex(
  options: FlexSearch.IndexOptions<string> = {},
) {
  return new FlexSearch.Index({
    ...options,
    tokenize: 'forward',
  });
}
